import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

export type BeforeNavigateCallback = (target: string) => Promise<void>;

interface NavigationBlockerContextValue {
  /** Navigate to path, running beforeNavigate callback first if registered (e.g. for auto-saving) */
  navigateWithInterceptor: (target: string | number) => void;
  /** Register a callback to run before navigation (e.g. save unsaved image). Call with null to unregister. */
  setBeforeNavigate: (callback: BeforeNavigateCallback | null) => void;
}

const NavigationBlockerContext = createContext<
  NavigationBlockerContextValue | undefined
>(undefined);

export function NavigationBlockerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const beforeNavigateRef = useRef<BeforeNavigateCallback | null>(null);
  const [, forceUpdate] = useState({});

  const setBeforeNavigate = useCallback(
    (callback: BeforeNavigateCallback | null) => {
      beforeNavigateRef.current = callback;
      forceUpdate({});
    },
    [],
  );

  const navigateWithInterceptor = useCallback(
    (target: string | number) => {
      const targetPath = typeof target === 'number' ? '' : target;
      const callback = beforeNavigateRef.current;

      const doNavigate = () => {
        if (typeof target === 'number') {
          navigate(target);
        } else {
          navigate(target);
        }
      };

      if (callback) {
        callback(targetPath)
          .then(() => {
            doNavigate();
          })
          .catch(() => {
            // Callback handles its own errors (e.g. Notifier.error)
            // Don't navigate on error
          });
      } else {
        doNavigate();
      }
    },
    [navigate],
  );

  const value: NavigationBlockerContextValue = React.useMemo(
    () => ({
      navigateWithInterceptor,
      setBeforeNavigate,
    }),
    [navigateWithInterceptor, setBeforeNavigate],
  );

  return (
    <NavigationBlockerContext.Provider value={value}>
      {children}
    </NavigationBlockerContext.Provider>
  );
}

export function useNavigationBlocker(): NavigationBlockerContextValue {
  const context = useContext(NavigationBlockerContext);
  if (context === undefined) {
    throw new Error(
      'useNavigationBlocker must be used within a NavigationBlockerProvider',
    );
  }
  return context;
}
